import { httpClient } from './httpClient';
import { tokenStorage } from '../utils/tokenStorage';
import { getDeviceFingerprint } from '../utils/deviceFingerprint';
import type {
  MfaChallengeResponse,
  MfaVerifyRequest,
  MfaTotpSetupResponse,
  MfaStatusResponse,
  MfaBackupCodesResponse,
  TrustedDeviceInfo,
  MfaMethod,
} from '../types';

// Types for authentication API
export interface LoginRequest {
  email: string;
  password: string;
}

export type PersonaType = 'EXPERT_WITNESS' | 'SOLICITOR';

export interface RegisterRequest {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  title: string;
  gmcNumber?: string;
  department?: string;
  specialization?: string;
  inviteCode?: string;
  personaType?: PersonaType;
}

export interface AuthUser {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  title: string;
  customerNumber: string;
  gmcNumber: string;
  department: string;
  role: 'USER' | 'ADMIN';
  personaType: PersonaType;
  createdAt: string;
  consentsCompleted: boolean;
  emailVerified: boolean;
}

export interface ConsentRecord {
  consentType: 'TERMS_OF_SERVICE' | 'PRIVACY_POLICY';
  consentVersion: string;
  granted: boolean;
}

export interface CompleteProfileData {
  consents: ConsentRecord[];
}

export interface UpdateProfileData {
  phoneNumber?: string;
  specialization?: string;
  referralSource?: string;
  personaType?: PersonaType;
}

export interface ConsentStatusResponse {
  consentsCompleted: boolean;
  consents: Array<{
    consentType: string;
    consentVersion: string;
    granted: boolean;
    grantedAt: string;
  }>;
}

export interface AuthResponse {
  accessToken: string;
  refreshToken: string;
  tokenType: string;
  expiresIn: number;
  user: AuthUser;
}

export interface TokenValidationResponse {
  valid: boolean;
  user: AuthUser;
}

// Custom error class for authentication API errors
export class AuthApiError extends Error {
  constructor(
    message: string,
    public code: string,
    public status: number,
    public details?: any
  ) {
    super(message);
    this.name = 'AuthApiError';
  }
}

// Token manager - delegates to centralised tokenStorage.
// Provides backward-compatible API for consumers that import { tokenManager } from authApi.
export const tokenManager = {
  storeTokens(authResponse: AuthResponse): void {
    tokenStorage.setToken(authResponse.accessToken);
    tokenStorage.setRefreshToken(authResponse.refreshToken);
    tokenStorage.storeUser(authResponse.user);
  },

  getAccessToken(): string | null {
    return tokenStorage.getToken();
  },

  getRefreshToken(): string | null {
    return tokenStorage.getRefreshToken();
  },

  getStoredUser(): AuthUser | null {
    return tokenStorage.getStoredUser();
  },

  clearTokens(): void {
    tokenStorage.clearAll();
  },

  isTokenExpired(token: string): boolean {
    return tokenStorage.isTokenExpired(token);
  },

  getUserFromToken(token: string): Partial<AuthUser> | null {
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      return {
        id: payload.sub,
        email: payload.email,
        firstName: payload.firstName,
        lastName: payload.lastName,
        title: payload.title,
        role: payload.role || 'USER'
      };
    } catch {
      return null;
    }
  }
};

// Authentication API service
export const authApi = {
  /**
   * Login user with email and password.
   * Returns AuthResponse on success, or MfaChallengeResponse when MFA is required.
   */
  async login(credentials: LoginRequest): Promise<AuthResponse | MfaChallengeResponse> {
    try {
      const fingerprint = await getDeviceFingerprint();

      // Use raw fetch so we can set the custom header and inspect the shape of the response
      const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api';
      const res = await fetch(`${API_BASE_URL}/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Device-Fingerprint': fingerprint,
        },
        body: JSON.stringify(credentials),
        credentials: 'include',
      });

      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        throw new AuthApiError(
          errorData.message || 'Login failed',
          errorData.error || 'LOGIN_FAILED',
          res.status,
          errorData
        );
      }

      const json = await res.json();
      // Unwrap ApiResponse wrapper if present
      const payload = json && typeof json === 'object' && 'data' in json ? json.data : json;

      // If the response contains mfaChallengeToken, MFA is required
      if (payload.mfaChallengeToken) {
        return payload as MfaChallengeResponse;
      }

      // Normal auth response — store tokens
      const authResponse = payload as AuthResponse;
      tokenManager.storeTokens(authResponse);
      return authResponse;
    } catch (error: any) {
      if (error instanceof AuthApiError) throw error;
      if (error.code) {
        throw new AuthApiError(
          error.message || 'Login failed',
          error.code,
          error.status || 401,
          error.details
        );
      }
      throw new AuthApiError(
        'Login failed. Please check your credentials and try again.',
        'LOGIN_FAILED',
        401
      );
    }
  },

  /**
   * Register new user
   */
  async register(userData: RegisterRequest): Promise<AuthResponse> {
    try {
      const response = await httpClient.post<AuthResponse>('/auth/register', userData);
      
      // Store tokens after successful registration
      tokenManager.storeTokens(response);
      
      return response;
    } catch (error: any) {
      if (error.code) {
        throw new AuthApiError(
          error.message || 'Registration failed',
          error.code,
          error.status || 400,
          error.details
        );
      }
      throw new AuthApiError(
        'Registration failed. Please check your information and try again.',
        'REGISTRATION_FAILED',
        400
      );
    }
  },

  /**
   * Validate JWT token
   */
  async validateToken(token: string): Promise<TokenValidationResponse> {
    try {
      const response = await httpClient.post<TokenValidationResponse>('/auth/validate', { token });
      return response;
    } catch (error: any) {
      if (error.code) {
        throw new AuthApiError(
          error.message || 'Token validation failed',
          error.code,
          error.status || 401,
          error.details
        );
      }
      throw new AuthApiError(
        'Token validation failed',
        'TOKEN_VALIDATION_FAILED',
        401
      );
    }
  },

  /**
   * Refresh access token using refresh token
   */
  async refreshToken(refreshToken: string): Promise<AuthResponse> {
    try {
      const response = await httpClient.post<AuthResponse>('/auth/refresh', { refreshToken });
      
      // Store new tokens
      tokenManager.storeTokens(response);
      
      return response;
    } catch (error: any) {
      // Clear tokens if refresh fails
      tokenManager.clearTokens();
      
      if (error.code) {
        throw new AuthApiError(
          error.message || 'Token refresh failed',
          error.code,
          error.status || 401,
          error.details
        );
      }
      throw new AuthApiError(
        'Session expired. Please log in again.',
        'TOKEN_REFRESH_FAILED',
        401
      );
    }
  },

  /**
   * Logout user — always notifies the backend so the refresh token is
   * revoked server-side and the httpOnly cookies are cleared. Sends the
   * in-memory refresh token if we have it, otherwise the backend falls
   * back to reading the value from the httpOnly cookie.
   */
  async logout(): Promise<void> {
    try {
      const refreshToken = tokenManager.getRefreshToken();
      await httpClient.post('/auth/logout', { refreshToken: refreshToken ?? '' }).catch(() => {
        // Server-side revoke failed — local tokens are still cleared below.
      });
    } finally {
      tokenManager.clearTokens();
    }
  },

  /**
   * Get current user profile
   */
  async getCurrentUser(): Promise<AuthUser> {
    try {
      const response = await httpClient.get<AuthUser>('/auth/me');
      return response;
    } catch (error: any) {
      if (error.code) {
        throw new AuthApiError(
          error.message || 'Failed to get user profile',
          error.code,
          error.status || 401,
          error.details
        );
      }
      throw new AuthApiError(
        'Failed to get user profile',
        'USER_PROFILE_FAILED',
        401
      );
    }
  },

  /**
   * Request password reset (forgot password)
   */
  async forgotPassword(email: string): Promise<void> {
    try {
      await httpClient.post('/auth/forgot-password', { email });
    } catch (error: any) {
      // Always succeed for security (prevent email enumeration)
      // The backend will handle this properly
    }
  },

  /**
   * Reset password using reset token
   */
  async resetPassword(token: string, newPassword: string): Promise<void> {
    try {
      await httpClient.post('/auth/reset-password', { token, newPassword });
    } catch (error: any) {
      if (error.code) {
        throw new AuthApiError(
          error.message || 'Password reset failed',
          error.code,
          error.status || 400,
          error.details
        );
      }
      throw new AuthApiError(
        'Password reset failed. The link may be invalid or expired.',
        'PASSWORD_RESET_FAILED',
        400
      );
    }
  },

  /**
   * Validate password reset token
   */
  async validateResetToken(token: string): Promise<boolean> {
    try {
      const response = await httpClient.get<{ valid: boolean }>(`/auth/validate-reset-token?token=${encodeURIComponent(token)}`);
      return response.valid;
    } catch (error: any) {
      return false;
    }
  },

  async completeProfile(data: CompleteProfileData): Promise<{ message: string }> {
    return httpClient.post<{ message: string }>('/auth/complete-profile', data);
  },

  async updateProfile(data: UpdateProfileData): Promise<{ message: string }> {
    return httpClient.patch<{ message: string }>('/auth/profile', data);
  },

  async getConsentStatus(): Promise<ConsentStatusResponse> {
    return httpClient.get<ConsentStatusResponse>('/auth/consents/status');
  },

  async verifyEmail(token: string): Promise<{ verified: boolean; message: string }> {
    return httpClient.post<{ verified: boolean; message: string }>('/auth/verify-email', { token });
  },

  async resendVerification(): Promise<{ message: string }> {
    return httpClient.post<{ message: string }>('/auth/resend-verification');
  },

  async changeEmail(newEmail: string, currentPassword: string): Promise<{ message: string }> {
    return httpClient.patch<{ message: string }>('/auth/email', { newEmail, currentPassword });
  },

  async heartbeat(): Promise<void> {
    await httpClient.post('/auth/heartbeat');
  },

  /**
   * Exchange a one-time impersonation code for tokens.
   * Called from the /impersonate page after admin initiates impersonation.
   */
  async exchangeImpersonationCode(code: string): Promise<AuthResponse> {
    const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api';
    const res = await fetch(`${API_BASE_URL}/auth/impersonate/exchange`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ code }),
      credentials: 'include',
    });

    if (!res.ok) {
      const errorData = await res.json().catch(() => ({}));
      throw new AuthApiError(
        errorData.message || 'Invalid or expired impersonation code',
        errorData.error || 'IMPERSONATION_FAILED',
        res.status
      );
    }

    const json = await res.json();
    const payload = json && typeof json === 'object' && 'data' in json ? json.data : json;
    const authResponse = payload as AuthResponse;
    tokenManager.storeTokens(authResponse);
    return authResponse;
  }
};

// ── MFA API ───────────────────────────────────────────────────────────

export const mfaApi = {
  /** Verify an MFA code during login (pre-auth, uses challenge token). */
  async verify(request: MfaVerifyRequest): Promise<AuthResponse> {
    const fingerprint = await getDeviceFingerprint();
    const response = await httpClient.post<AuthResponse>('/auth/mfa/verify', {
      ...request,
      deviceFingerprint: fingerprint,
    });
    tokenManager.storeTokens(response);
    return response;
  },

  /** Send an OTP (email/sms) during login challenge. Returns the new challenge token. */
  async sendOtp(challengeToken: string, method: MfaMethod): Promise<string> {
    const response = await httpClient.post<{ message: string; mfaChallengeToken: string }>('/auth/mfa/send-otp', {
      mfaChallengeToken: challengeToken,
      method,
    });
    return response.mfaChallengeToken;
  },

  /** Get current MFA status (authenticated). */
  async getStatus(): Promise<MfaStatusResponse> {
    return httpClient.get<MfaStatusResponse>('/auth/mfa/status');
  },

  /** Initiate TOTP setup — returns secret + QR URI. */
  async setupTotp(): Promise<MfaTotpSetupResponse> {
    return httpClient.post<MfaTotpSetupResponse>('/auth/mfa/totp/setup');
  },

  /** Confirm TOTP setup with a 6-digit code from the authenticator app. */
  async confirmTotp(code: string): Promise<void> {
    await httpClient.post('/auth/mfa/totp/confirm', { code });
  },

  /** Enable email OTP method. */
  async enableEmail(): Promise<void> {
    await httpClient.post('/auth/mfa/email/enable');
  },

  /** Start SMS setup with phone number (E.164). */
  async setupSms(phoneNumber: string): Promise<void> {
    await httpClient.post('/auth/mfa/sms/setup', { phoneNumber });
  },

  /** Confirm SMS setup with verification code. */
  async confirmSms(code: string): Promise<void> {
    await httpClient.post('/auth/mfa/sms/confirm', { code });
  },

  /** Enable MFA with preferred method (returns backup codes). */
  async enable(preferredMethod: MfaMethod): Promise<MfaBackupCodesResponse> {
    return httpClient.post<MfaBackupCodesResponse>('/auth/mfa/enable', { preferredMethod });
  },

  /** Disable MFA (requires current password). */
  async disable(currentPassword: string): Promise<void> {
    await httpClient.post('/auth/mfa/disable', { currentPassword });
  },

  /** Regenerate backup codes. */
  async regenerateBackupCodes(): Promise<MfaBackupCodesResponse> {
    return httpClient.post<MfaBackupCodesResponse>('/auth/mfa/backup-codes/regenerate');
  },

  /** List trusted devices. */
  async getDevices(): Promise<TrustedDeviceInfo[]> {
    return httpClient.get<TrustedDeviceInfo[]>('/auth/mfa/devices');
  },

  /** Revoke a specific trusted device. */
  async revokeDevice(deviceId: string): Promise<void> {
    await httpClient.delete(`/auth/mfa/devices/${deviceId}`);
  },

  /** Revoke all trusted devices. */
  async revokeAllDevices(): Promise<void> {
    await httpClient.delete('/auth/mfa/devices');
  },

  /** Dismiss the MFA encouragement banner. */
  async dismissBanner(): Promise<void> {
    await httpClient.post('/auth/mfa/dismiss-banner');
  },
};
